import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as ReadyIcon,
  Warning as DuplicateIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  CloudUpload as ImportIcon,
} from '@mui/icons-material';
import CompanyDuplicateResolutionPanel from './CompanyDuplicateResolutionPanel';

const STATUS_CONFIG = {
  ready: {
    icon: ReadyIcon,
    label: 'Ready',
    chipColor: 'success',
  },
  duplicate: {
    icon: DuplicateIcon,
    label: 'Duplicate',
    chipColor: 'warning',
  },
  error: {
    icon: ErrorIcon,
    label: 'Error',
    chipColor: 'error',
  },
};

const CompanyImportReviewModal = ({ open, onClose, importedCompanies, onImport }) => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companies, setCompanies] = useState(importedCompanies);

  useEffect(() => {
    setCompanies(importedCompanies);
  }, [importedCompanies]);

  const summary = useMemo(() => {
    const ready = companies.filter((c) => c.status === 'ready').length;
    const duplicates = companies.filter((c) => c.status === 'duplicate').length;
    const errors = companies.filter((c) => c.status === 'error').length;
    return { total: companies.length, ready, duplicates, errors };
  }, [companies]);

  const filteredCompanies = useMemo(() => {
    let result = companies;

    if (filter !== 'all') {
      result = result.filter((c) => c.status === filter);
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter((c) =>
        c.name?.toLowerCase().includes(search)
        || c.tradeLicenseNumber?.toLowerCase().includes(search)
        || c.parentCompanyName?.toLowerCase().includes(search));
    }

    return result;
  }, [companies, filter, searchTerm]);

  const handleResolutionComplete = (resolution) => {
    setCompanies((prev) =>
      prev.map((c) =>
        c._rowIndex === resolution.companyIndex
          ? { ...c, status: 'ready', resolution }
          : c));
    setSelectedCompany(null);
  };

  const handleImportReady = () => {
    const readyCompanies = companies.filter((c) => c.status === 'ready');
    onImport(readyCompanies);
  };

  const handleImportAll = () => {
    if (summary.duplicates === 0 && summary.errors === 0) {
      onImport(companies);
    }
  };

  const canImportAll = summary.duplicates === 0 && summary.errors === 0;
  const issueCount = summary.duplicates + summary.errors;

  return (
    <>
      <Dialog
        open={open && !selectedCompany}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Company Import Review</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Paper sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
            <Box display="flex" gap={3} justifyContent="center" alignItems="center">
              <Box textAlign="center">
                <Typography variant="h4" color="primary">{summary.total}</Typography>
                <Typography variant="caption" color="textSecondary">Total</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4" color="success.main">{summary.ready}</Typography>
                <Typography variant="caption" color="textSecondary">Ready</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4" color="warning.main">{summary.duplicates}</Typography>
                <Typography variant="caption" color="textSecondary">Duplicates</Typography>
              </Box>
              <Box textAlign="center">
                <Typography variant="h4" color="error.main">{summary.errors}</Typography>
                <Typography variant="caption" color="textSecondary">Errors</Typography>
              </Box>
            </Box>
          </Paper>

          <Box display="flex" gap={2} mb={2}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Filter</InputLabel>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                label="Filter"
              >
                <MenuItem value="all">All ({summary.total})</MenuItem>
                <MenuItem value="ready">Ready ({summary.ready})</MenuItem>
                <MenuItem value="duplicate">Duplicates ({summary.duplicates})</MenuItem>
                <MenuItem value="error">Errors ({summary.errors})</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              placeholder="Search by name, license, or parent..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Box>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell width="50">#</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>License</TableCell>
                  <TableCell>Structure / Parent</TableCell>
                  <TableCell width="150">Status</TableCell>
                  <TableCell width="100" align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCompanies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary">No companies found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCompanies.map((company) => {
                    const statusConfig = STATUS_CONFIG[company.status];
                    const StatusIcon = statusConfig.icon;

                    return (
                      <TableRow key={company._rowIndex} hover>
                        <TableCell>{company._rowIndex + 1}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{company.name}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {company.country} · {company.governanceTypeCode}
                          </Typography>
                        </TableCell>
                        <TableCell>{company.tradeLicenseNumber}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{company.structureType}</Typography>
                          {company.parentCompanyName && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              Parent: {company.parentCompanyName}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={<StatusIcon />}
                            label={statusConfig.label}
                            color={statusConfig.chipColor}
                            size="small"
                          />
                          {company.errors?.length > 0 && (
                            <Tooltip title={company.errors.join(', ')}>
                              <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                                {company.errors.length} error(s)
                              </Typography>
                            </Tooltip>
                          )}
                          {company.matches?.length > 0 && (
                            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                              {company.matches.length} match(es)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {(company.status === 'duplicate' || company.status === 'error') && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setSelectedCompany(company)}
                            >
                              Resolve
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {issueCount > 0 && (
            <Box mt={2}>
              <Chip
                icon={<ErrorIcon />}
                label={`Please resolve ${issueCount} issue${issueCount > 1 ? 's' : ''} before importing all companies`}
                color="warning"
                sx={{ width: '100%', justifyContent: 'flex-start' }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancel Import
          </Button>
          {summary.ready > 0 && (
            <Button
              variant="outlined"
              onClick={handleImportReady}
              startIcon={<ImportIcon />}
            >
              Import Ready Only ({summary.ready})
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleImportAll}
            disabled={!canImportAll}
            startIcon={<ImportIcon />}
          >
            Import All ({summary.total})
          </Button>
        </DialogActions>
      </Dialog>

      {selectedCompany && (
        <CompanyDuplicateResolutionPanel
          open={!!selectedCompany}
          company={selectedCompany}
          onClose={() => setSelectedCompany(null)}
          onResolve={handleResolutionComplete}
        />
      )}
    </>
  );
};

export default CompanyImportReviewModal;
